// Auth-facing API surface for the web client (spec #33 – #40).
import { fetchJson } from './http';
import type { AuthSession } from './types';

interface AuthEnvelope {
  access_token: string;
  refresh_token: string;
  /** ISO timestamp string OR number of seconds until expiry */
  expires_at?: string;
  expires_in?: number;
  session_id?: string;
  user: { id: string; username: string; email: string };
}

function toSession(env: AuthEnvelope): AuthSession {
  // Compute a proper ISO expiry timestamp: the API returns either
  // `expires_at` (ISO string) or `expires_in` (seconds from now).
  let expiresAt: string;
  if (env.expires_at) {
    expiresAt = env.expires_at;
  } else if (typeof env.expires_in === 'number') {
    expiresAt = new Date(Date.now() + env.expires_in * 1000).toISOString();
  } else {
    // Fallback: assume 1 hour from now if neither field is present.
    expiresAt = new Date(Date.now() + 3600 * 1000).toISOString();
  }
  return {
    userId: env.user.id,
    username: env.user.username,
    email: env.user.email,
    accessToken: env.access_token,
    refreshToken: env.refresh_token,
    expiresAt,
  };
}

function getAnonToken(): string | null {
  return null;
}

export const authApi = {
  async register(input: { username: string; email: string; password: string }) {
    const env = await fetchJson<AuthEnvelope>('/auth/register', getAnonToken, {
      method: 'POST',
      body: input,
      anonymous: true,
    });
    return toSession(env);
  },

  async login(input: { email: string; password: string }) {
    const env = await fetchJson<AuthEnvelope>('/auth/login', getAnonToken, {
      method: 'POST',
      body: input,
      anonymous: true,
    });
    return toSession(env);
  },

  async refresh(refreshToken: string) {
    const env = await fetchJson<AuthEnvelope>('/auth/refresh', getAnonToken, {
      method: 'POST',
      body: { refresh_token: refreshToken },
      anonymous: true,
    });
    return toSession(env);
  },

  async logout(refreshToken: string) {
    await fetchJson('/auth/logout', getAnonToken, {
      method: 'POST',
      body: { refresh_token: refreshToken },
      anonymous: true,
    });
  },

  async forgotPassword(email: string) {
    await fetchJson('/auth/forgot-password', getAnonToken, {
      method: 'POST',
      body: { email },
      anonymous: true,
    });
  },

  async resetPassword(input: { token: string; new_password: string }) {
    await fetchJson('/auth/reset-password', getAnonToken, {
      method: 'POST',
      body: input,
      anonymous: true,
    });
  },

  async verifyEmail(token: string) {
    const data = await fetchJson<{ verified: boolean }>('/auth/verify-email', getAnonToken, {
      method: 'POST',
      body: { token },
      anonymous: true,
    });
    return Boolean(data?.verified);
  },
};

export type AuthApi = typeof authApi;
